import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Clock } from "lucide-react";
import { COMPANY } from "@/lib/site-data";

const API = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "");

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "",subject: "",message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Backend URL is missing.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! Our team will get back to you within 1 business day.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
        toast.error("Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="relative bg-white py-20 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 grid-bg pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-700 mb-4">
            Get in touch
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.02] max-w-4xl">
            Let's keep your business <span className="text-red-600">running.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            Tell us about your infrastructure and we'll route you to the right engineer — usually
            within an hour during business days.
          </p>
        </div>
      </section>

      {/* Contact Layout */}
      <section className="bg-slate-50 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
          {/* Info */}
          <div className="lg:col-span-5 space-y-5">
            <div className="bg-blue-900 text-white rounded-2xl p-8 lg:p-10 relative overflow-hidden" data-testid="contact-info-card">
              <div className="absolute -bottom-24 -right-24 h-56 w-56 rounded-full bg-blue-700/40 blur-3xl pointer-events-none" />
              <div className="relative space-y-7">
                <h2 className="font-display text-2xl font-bold">Direct contact</h2>
                <a href={`tel:${COMPANY.phoneRaw}`} className="flex gap-4 items-start group" data-testid="contact-phone-link">
                  <div className="h-11 w-11 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-blue-200">Phone</div>
                    <div className="font-display text-lg font-bold group-hover:text-red-300 transition-colors">{COMPANY.phone}</div>
                  </div>
                </a>
                <a href={`mailto:${COMPANY.email}`} className="flex gap-4 items-start group" data-testid="contact-email-link">
                  <div className="h-11 w-11 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-blue-200">Email</div>
                    <div className="font-display text-lg font-bold break-all group-hover:text-red-300 transition-colors">{COMPANY.email}</div>
                  </div>
                </a>
                <div className="flex gap-4 items-start" data-testid="contact-address">
                  <div className="h-11 w-11 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-blue-200">Office</div>
                    <div className="font-display text-base leading-relaxed">{COMPANY.address}</div>
                  </div>
                </div>
                <div className="flex gap-4 items-start" data-testid="contact-hours">
                  <div className="h-11 w-11 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-blue-200">Support hours</div>
                    <div className="font-display text-base">24 / 7 — incident response & monitoring</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white" data-testid="contact-map">
              <iframe
                title="R-Tech Computer Office Location"
                src="https://www.google.com/maps?q=Andheri+East+Mahakali+Road+Mumbai+400093&output=embed"
                width="100%"
                height="320"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-white border border-slate-200 rounded-2xl p-8 lg:p-10"
            >
              <h2 className="font-display text-2xl font-bold text-slate-900 mb-2">
                Send us a message
              </h2>
              <p className="text-sm text-slate-500 mb-7">
                We respond to most enquiries within 1 business day.
              </p>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name" name="name" value={form.name} onChange={handleChange} placeholder="Your full name" />
                <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@company.com" />
                <Field label="Phone" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 9XXXX XXXXX" />
                <Field label="Subject (optional)" name="subject" value={form.subject || ""} onChange={handleChange} placeholder="What is this about?" optional />
              </div>
              <div className="mt-5">
                <label className="block text-xs font-bold uppercase tracking-[0.18em] text-slate-700 mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={6}
                  data-testid="contact-input-message"
                  placeholder="Tell us about your infrastructure & support needs..."
                  className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="mt-7 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-7 py-3.5 rounded-md font-semibold transition-all hover:-translate-y-0.5"
              >
                {submitting ? "Sending..." : (<>Send Message <Send className="h-4 w-4" /></>)}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text", optional }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-[0.18em] text-slate-700 mb-2">
        {label} {optional && <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`contact-input-${name}`}
        className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
      />
    </div>
  );
}
